<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGenerationControlling',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);

  const $refs = {
    get items() {
      return items.value;
    },
  };

  const getAllRows = (...args) => {
    return values.value?.rows ?? [];
  };
  const getInvoiceGroups = (...args) => {
    const [rows] = args;
    const visited = [];
    const result = [];
    for (let row of rows) {
      const value = row.invoice_group_id;

      if (visited.indexOf(value) > -1) {
        continue;
      }

      result.push({ value, text: value ? value.toUpperCase() : 'BLANK' });
      visited.push(value);
    }
    return result;
  };
  const createGroups = (...args) => {
    const [rows] = args;

    return _.chain(rows)
      .groupBy((p) => `${p.partner_id}${p.rental_id}`)
      .map((rows, group) => {
        const visibleRows = _.filter(rows, isRowVisible);
        if (!visibleRows.length) {
          return;
        }

        return {
          ...visibleRows[0],
          rows: visibleRows,
          totals: calculateTotals(visibleRows),
        };
      })
      .filter((p) => !!p)
      .value();
  };
  const calculateTotals = (...args) => {
    const [rows] = args;
    const totals = {};

    for (let row of rows) {
      for (let colName of values.value.columns) {
        if (!row[colName] || row[colName].net == null) {
          continue;
        }

        const net = row[colName].net;

        if (totals[colName] == null) {
          totals[colName] = net;
          continue;
        }

        totals[colName] += net;
      }
    }

    return totals;
  };
  const applyFilters = (...args) => {
    groups.value = createGroups(getAllRows());
    pager.value.setItems(groups.value);
  };
  const isRowVisible = (...args) => {
    const [row] = args;
    const selectedGroups = selectedInvoiceGroups.value;

    return (
      selectedGroups.length === 0 ||
      selectedGroups.includes(row.invoice_group_id)
    );
  };
  const scrollItems = (...args) => {
    const [pagerInstance, refs] = args;
    const [m, n] = pagerInstance.history.slice(-2);

    const $el = refs.items;

    if (!$el) return;

    if (m < n) {
      //forward -> scroll to the top
      $el.scrollTop = 0;
    } else {
      //reward -> scroll to the bottom
      $el.scrollTop = $el.scrollHeight;
    }
  };

  const pageSize = ref(10);
  const rowHeaderColumnWidth = ref('auto');
  const dataColumnWidth = ref('200px');
  const resizeTrigger = ref(0);
  const selectedInvoiceGroups = ref([]);
  const selectedItems = ref([]);
  const ts = ref(null);
  const values = ref(null);
  const invoiceGroups = ref(null);
  const groups = ref(null);
  const filterInputValue = ref(null);
  const pager = ref(null);
  const laggingApplyFilters = ref(null);
  const ux = ref([]);
  const b_2248226175642056_style = computed(() => ({
    'max-width': '1280px',
  }));
  const b_7172922356531116_style = computed(() => ({
    width: dataColumnWidth.value,
  }));
  const b_342583468269099_style = computed(() => ({
    width: rowHeaderColumnWidth.value,
  }));
  const b_6477172378042748_style = computed(() => ({
    width: dataColumnWidth.value,
  }));

  const $data = computed(() => ({
    pageSize: pageSize.value,
    rowHeaderColumnWidth: rowHeaderColumnWidth.value,
    dataColumnWidth: dataColumnWidth.value,
    resizeTrigger: resizeTrigger.value,
    selectedInvoiceGroups: selectedInvoiceGroups.value,
    selectedItems: selectedItems.value,
    ts: ts.value,
    values: values.value,
    invoiceGroups: invoiceGroups.value,
    groups: groups.value,
    filterInputValue: filterInputValue.value,
    pager: pager.value,
    laggingApplyFilters: laggingApplyFilters.value,
    ux: ux.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
    b_7172922356531116_style: b_7172922356531116_style.value,
    b_342583468269099_style: b_342583468269099_style.value,
    b_6477172378042748_style: b_6477172378042748_style.value,
  }));

  const $$load_values = async () => {
    return $fn.value.fetch('view_draft_invoice_lines');
  };
  const $$load_invoiceGroups = async () => {
    return getInvoiceGroups(getAllRows());
  };
  const $$load_groups = async () => {
    return createGroups(getAllRows());
  };
  const $$load_pager = async () => {
    return $fn.value.pager(groups.value, $data.value.pageSize);
  };
  const $$load_laggingApplyFilters = async () => {
    return _.debounce(_.throttle(applyFilters, 300), 500);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'pageSize' },
      { name: 'rowHeaderColumnWidth' },
      { name: 'dataColumnWidth' },
      { name: 'resizeTrigger' },
      {
        name: 'selectedInvoiceGroups',
        cache: {
          type: 'local',
          key: 'UcInvoiceGenerationControlling.selectedInvoiceGroups',
        },
      },
      { name: 'selectedItems' },
      { name: 'ts' },
      { name: 'values' },
      { name: 'invoiceGroups', dependsOn: ['values'] },
      { name: 'groups', dependsOn: ['values'] },
      { name: 'filterInputValue' },
      { name: 'pager', dependsOn: ['groups'] },
      { name: 'laggingApplyFilters' },
      { name: 'ux' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
      { name: 'b_7172922356531116_style', nodeId: '7172922356531116' },
      { name: 'b_342583468269099_style', nodeId: '342583468269099' },
      { name: 'b_6477172378042748_style', nodeId: '6477172378042748' },
    ],
    loaders: {
      values: $$load_values,
      invoiceGroups: $$load_invoiceGroups,
      groups: $$load_groups,
      pager: $$load_pager,
      laggingApplyFilters: $$load_laggingApplyFilters,
    },
    ux: {},
    dataRefs: {
      pageSize,
      rowHeaderColumnWidth,
      dataColumnWidth,
      resizeTrigger,
      selectedInvoiceGroups,
      selectedItems,
      ts,
      values,
      invoiceGroups,
      groups,
      filterInputValue,
      pager,
      laggingApplyFilters,
      ux,
      b_2248226175642056_style,
      b_7172922356531116_style,
      b_342583468269099_style,
      b_6477172378042748_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_8640370005096962_script_952uk4sed_value_callable = async (
    $event
  ) => {
    return laggingApplyFilters.value();
  };
  const act_8640370005096962_script_952uk4sed_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8640370005096962_script_952uk4sed_value_callable($event);
      },
    };
  };
  const act_6214767824713988_showModal_z36k6tppz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.scope.invoice.invoice_id,
      },
    };
  };
  const act_4207795475682514_script_wwat5jafj_value_callable = async (
    $event
  ) => {
    return (
      pager.value.setCurrentPage($event.data),
      scrollItems(pager.value, $refs)
    );
  };
  const act_4207795475682514_script_wwat5jafj_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4207795475682514_script_wwat5jafj_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_mw1bq2gia =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tryjmm9ea =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mw1bq2gia,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tryjmm9ea],
    event: 'reload',
  });
  const act_2285343489561581_reloadComponentData_d0lqcb60l =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_2285343489561581_clickPreventStop_52xth2u0a =
    new actionTypes.EventHandlerImpl({
      action: act_2285343489561581_reloadComponentData_d0lqcb60l,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_2285343489561581_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2285343489561581_clickPreventStop_52xth2u0a],
      event: 'click.prevent.stop',
    });
  const act_8640370005096962_script_952uk4sed = new actionTypes.ScriptAction({
    actionArgs: act_8640370005096962_script_952uk4sed_getActionArgs,
  });
  const inst_evh_8640370005096962_change_owuj3t3gr =
    new actionTypes.EventHandlerImpl({
      action: act_8640370005096962_script_952uk4sed,
      event: 'change',
      displayName: 'script',
    });
  const inst_evh_8640370005096962_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8640370005096962_change_owuj3t3gr],
    event: 'change',
  });
  const act_6214767824713988_showModal_z36k6tppz =
    new actionTypes.ShowModalAction({
      actionArgs: act_6214767824713988_showModal_z36k6tppz_getActionArgs,
    });
  const inst_evh_6214767824713988_clickPreventStop_gjgz4eaci =
    new actionTypes.EventHandlerImpl({
      action: act_6214767824713988_showModal_z36k6tppz,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6214767824713988_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6214767824713988_clickPreventStop_gjgz4eaci],
      event: 'click.prevent.stop',
    });
  const act_4207795475682514_script_wwat5jafj = new actionTypes.ScriptAction({
    actionArgs: act_4207795475682514_script_wwat5jafj_getActionArgs,
  });
  const inst_evh_4207795475682514_updateModelValue_7bj5r880f =
    new actionTypes.EventHandlerImpl({
      action: act_4207795475682514_script_wwat5jafj,
      event: 'update:modelValue',
      displayName: 'script',
    });
  const inst_evh_4207795475682514_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4207795475682514_updateModelValue_7bj5r880f],
      event: 'update:modelValue',
    });

  const actions = {
    inst_evh_7315092382398562_reload_tryjmm9ea,
    inst_evh_7315092382398562_reload,
    inst_evh_2285343489561581_clickPreventStop_52xth2u0a,
    reload: inst_evh_2285343489561581_clickPreventStop_52xth2u0a,
    inst_evh_2285343489561581_clickPreventStop,
    inst_evh_8640370005096962_change_owuj3t3gr,
    inst_evh_8640370005096962_change,
    inst_evh_6214767824713988_clickPreventStop_gjgz4eaci,
    inst_evh_6214767824713988_clickPreventStop,
    inst_evh_4207795475682514_updateModelValue_7bj5r880f,
    inst_evh_4207795475682514_updateModelValue,
  };

  const evh_7315092382398562_reload_tryjmm9ea = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tryjmm9ea.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2285343489561581_clickPreventStop_52xth2u0a = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2285343489561581_clickPreventStop_52xth2u0a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2285343489561581_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2285343489561581_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8640370005096962_change_owuj3t3gr = async (event, scope) => {
    return await actions.inst_evh_8640370005096962_change_owuj3t3gr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8640370005096962_change = async (event, scope) => {
    return await actions.inst_evh_8640370005096962_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6214767824713988_clickPreventStop_gjgz4eaci = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6214767824713988_clickPreventStop_gjgz4eaci.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6214767824713988_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6214767824713988_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_updateModelValue_7bj5r880f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4207795475682514_updateModelValue_7bj5r880f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_4207795475682514_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div
      class="card h-100 overflow-hidden"
      v-if="loaded"
      :style="b_2248226175642056_style"
    >
      <div class="card-header">
        <ItpBox>
          <ItpBox>
            <ItpButton
              icon="fa-sync-alt"
              :tooltip="gettext('Reload data from the server')"
              :spinning="actions.reload && actions.reload.isRunning"
              variant="light"
              :text="pgettext('InvoiceGenerationControlling', 'Reload data')"
              class="me-2"
              @click.prevent.stop="
                evh_2285343489561581_clickPreventStop($event, {})
              "
            >
            </ItpButton>
            <BDropdown
              :text="pgettext('InvoiceGenerationControlling', 'Invoice groups')"
              variant="light"
            >
              <BDropdownForm>
                <BFormCheckbox
                  :value="item.value"
                  :key="item.value"
                  v-model="selectedInvoiceGroups"
                  :name="item.value"
                  v-for="item in invoiceGroups"
                  @change="evh_8640370005096962_change($event, { item })"
                >
                  {{ item.text }}
                </BFormCheckbox>
              </BDropdownForm>
            </BDropdown>
          </ItpBox>
          <div class="d-flex flex-row align-items-center ms-auto text-center">
            <div
              :key="colName"
              class="px-2"
              v-for="colName in values.columns"
              :style="b_7172922356531116_style"
            >
              {{ colName }}
            </div>
          </div>
        </ItpBox>
      </div>
      <ul class="list-group list-group-flush h-100 overflow-auto" ref="items">
        <li
          :key="item.id"
          :class="{
            'list-group-item': true,
            'd-flex': true,
            'flex-row': true,
            'bg-gray-100': (pager.startIndex + index + 1) % 2 === 0,
          }"
          v-for="(item, index) in pager.items"
        >
          <ItpText class="pe-2"> #{{ pager.startIndex + index + 1 }} </ItpText>
          <ItpBox direction="column" class="flex-fill">
            <ItpBox direction="row" class="app-group-header mb-2">
              <div class="me-3 border rounded px-2">
                <UcCompanyMiniCard
                  no-icon
                  :item="{
                    id: item.partner_id,
                    name: item.partner,
                  }"
                >
                </UcCompanyMiniCard>
              </div>
              <div class="me-3 border rounded px-2" v-if="item.rental">
                <UcRentalMiniCard
                  no-icon
                  :item="{
                    id: item.rental_id,
                    name: item.rental,
                  }"
                >
                </UcRentalMiniCard>
              </div>
              <ItpText
                strong
                uppercase
                :text="item.site"
                class="border rounded px-2"
                v-if="item.site"
              >
              </ItpText>
            </ItpBox>
            <table class="app-group-rows table">
              <thead></thead>
              <tbody>
                <tr :key="row.id" v-for="(row, index) in item.rows">
                  <td
                    :style="{
                      width: rowHeaderColumnWidth,
                      'border-top-style': 'dotted',
                    }"
                  >
                    <ItpBox direction="column" alignItems="end">
                      <div>
                        <strong>{{ row.item }}</strong>
                      </div>
                      <div v-if="row.invoice_group_id">
                        {{ row.invoice_group_id }}
                      </div>
                      <UcMeterMiniCard
                        :item="{
                          id: row.meter_id,
                          serial_number: row.serial_number,
                          meter_type: row.meter_type,
                        }"
                        v-if="row.meter_id"
                      >
                      </UcMeterMiniCard>
                    </ItpBox>
                  </td>
                  <td
                    :key="colName"
                    class="px-2"
                    v-for="colName in values.columns"
                    :style="{
                      width: dataColumnWidth,
                      'border-top-style': 'dotted',
                    }"
                  >
                    <div
                      class="text-end"
                      v-if="row[colName] && row[colName].net != null"
                    >
                      <strong>
                        {{ number(row[colName].net) }} {{ row.currency }}
                      </strong>
                    </div>
                    <div
                      class="text-end"
                      v-if="row[colName] && row[colName].quantity != null"
                    >
                      <span>
                        {{ number(row[colName].quantity) }} {{ row.unit }}
                      </span>
                    </div>
                    <ItpBox
                      direction="column"
                      alignItems="end"
                      v-if="
                        row[colName] &&
                        row[colName].invoices &&
                        row[colName].invoices.length
                      "
                    >
                      <ItpBox
                        :key="idx"
                        direction="column"
                        alignItems="end"
                        data-name="invoice"
                        :class="{
                          'px-2 py-1 border shadow-sm': true,
                          'border-light': invoice.invoice_status !== 'draft',
                          'bg-yellow-200 border-yellow-600 rounded':
                            invoice.invoice_status === 'draft',
                        }"
                        v-for="(invoice, idx) in row[colName].invoices"
                      >
                        <div
                          class="text-end"
                          v-if="invoice.prior_reading_date != null"
                        >
                          <small>{{ date(invoice.prior_reading_date) }}</small>
                          &bull; {{ number(invoice.prior_reading_amount) }}
                          {{ invoice.prior_reading_unit }} <br />
                          <small>{{
                            date(invoice.current_reading_date)
                          }}</small>
                          &bull; {{ number(invoice.current_reading_amount) }}
                          {{ invoice.current_reading_unit }} <br />
                          x{{ number(invoice.billing_multiplier) }}
                        </div>
                        <ItpBox alignItems="center">
                          <UcInvoiceTypeBadge
                            :value="invoice.invoice_type"
                            :kind="invoice.invoice_kind"
                            :advance="invoice.is_advance"
                            abbreviated
                            class="me-2"
                          >
                          </UcInvoiceTypeBadge>
                          <a
                            href="#"
                            @click.prevent.stop="
                              evh_6214767824713988_clickPreventStop($event, {
                                item,
                                index,
                                row,
                                colName,
                                invoice,
                                idx,
                              })
                            "
                          >
                            <small class="text-nowrap">
                              {{ invoice.invoice_number }}
                            </small>
                          </a>
                        </ItpBox>
                        <UcContractMiniCard
                          no-icon
                          :item="{
                            id: invoice.contract_id,
                            contract_number: invoice.contract_number,
                          }"
                        >
                        </UcContractMiniCard>
                      </ItpBox>
                    </ItpBox>
                  </td>
                </tr>
                <tr>
                  <td class="text-end" :style="b_342583468269099_style">
                    Total:
                  </td>
                  <td
                    :key="colName"
                    direction="row"
                    class="px-2"
                    v-for="colName in values.columns"
                    :style="b_6477172378042748_style"
                  >
                    <div class="text-end" v-if="item.totals[colName] != null">
                      <strong>
                        {{ number(item.totals[colName]) }} {{ item.currency }}
                      </strong>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </ItpBox>
        </li>
      </ul>
      <div class="card-footer d-flex">
        <BPagination
          :modelValue="pager.currentPage"
          :totalRows="pager.totalRows"
          :perPage="pager.perPage"
          last-number
          first-number
          class="m-0 p-0 ms-auto"
          @update:modelValue="evh_4207795475682514_updateModelValue($event, {})"
        >
        </BPagination>
      </div>
    </div>
  </ItpUsercontrol>
</template>
